import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import DoneAllRoundedIcon from "@mui/icons-material/DoneAllRounded";
import { selectRealUser, selectUser } from "../../features/userSlice";
import { SectionHeader } from "../UI/Page";
import {
  getUserNotifications,
  markAllNotificationRead,
  markNotificationRead,
  normalizeDate,
} from "../../utilities";

const MAX_SHOWN = 4;

// Unread-notification strip for the homepage. Renders nothing when the
// inbox is empty — the header bell stays the always-available entry point.
const HomeNotifications = () => {
  const user = useSelector(selectUser);
  // Presence goes to the signed-in admin during a preview (see NotificationButton).
  const presenceId = useSelector(selectRealUser)?.id;
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    if (user?.email) {
      getUserNotifications(
        setNotifications,
        user.email,
        presenceId,
        window.location.pathname
      );
    }
  }, [user, presenceId]);

  if (!notifications.length) return null;

  const dismiss = (id) => {
    setNotifications((prev) => prev.filter((n) => n._id !== id));
    markNotificationRead(id);
  };

  const dismissAll = () => {
    markAllNotificationRead(user.email);
    setNotifications([]);
  };

  return (
    <section aria-labelledby="home-notifications-title">
      <SectionHeader
        title={
          <span id="home-notifications-title" className="flex items-center gap-2">
            Notifications
            <span className="inline-flex min-w-[1.5rem] items-center justify-center rounded-full bg-sky-500/15 px-2 py-0.5 text-xs font-semibold tabular-nums text-sky-700 dark:text-sky-300">
              {notifications.length}
            </span>
          </span>
        }
        actions={
          <button
            type="button"
            onClick={dismissAll}
            className="app-btn app-btn-ghost app-btn-sm"
          >
            <DoneAllRoundedIcon sx={{ fontSize: 17 }} />
            Mark all as read
          </button>
        }
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
        {notifications.slice(0, MAX_SHOWN).map((item) => (
          <div
            key={item._id}
            className="app-card flex items-start gap-3 rounded-2xl border p-4"
          >
            <span className="app-icon-tile h-10 w-10 rounded-xl bg-sky-500/15 text-sky-600 dark:text-sky-300">
              <NotificationsActiveRoundedIcon sx={{ fontSize: 20 }} />
            </span>
            <div className="min-w-0 flex-1">
              <div className="app-muted flex flex-wrap items-center gap-x-1.5 text-xs">
                <span className="font-medium">{item.from}</span>
                <span aria-hidden="true">·</span>
                <span>{normalizeDate(item.date)}</span>
              </div>
              <p className="mt-0.5 font-semibold leading-snug">{item.subject}</p>
              <p className="app-muted mt-0.5 text-sm leading-relaxed">
                {item.message}
              </p>
              {item.redirect && (
                <Link
                  to={item.redirect}
                  onClick={() => dismiss(item._id)}
                  className="app-link mt-2 inline-flex items-center gap-1 text-sm"
                >
                  Open
                  <ArrowForwardRoundedIcon sx={{ fontSize: 15 }} />
                </Link>
              )}
            </div>
            <button
              type="button"
              onClick={() => dismiss(item._id)}
              aria-label="Mark notification as read"
              title="Mark as read"
              className="app-icon-btn -mr-2 -mt-2 shrink-0"
            >
              <CloseRoundedIcon sx={{ fontSize: 18 }} />
            </button>
          </div>
        ))}
      </div>

      {notifications.length > MAX_SHOWN && (
        <p className="app-muted mt-3 text-sm">
          +{notifications.length - MAX_SHOWN} more in the bell menu at the top
          of the page.
        </p>
      )}
    </section>
  );
};

export default HomeNotifications;
